/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Divider,
  Form,
  Select,
  Input,
  Tooltip,
  Space,
  Descriptions,
  Table,
  Pagination,
  Spin,
  Checkbox,
  message,
  Modal,
  Radio,
  Tabs,
} from "antd";
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
  TableOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { getRequest, postRequest } from "../../util/service";
import FileUploadField from "../Attributes/FileUploadField";
import { generateImagesFromSmiles } from "../../util/ChemDrawUtils";
import { FilterCriteriaDisplay } from "../shared/FilterCriteriaDisplay";
import BreadcrumbTrail from "../shared/BreadcrumbTrail";
import { isRemovedJobParent } from "../../util/removedJobGuards";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const FormLabel = ({ label, tooltip }) => (
  <Space>
    <Text strong>{label}</Text>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
    </Tooltip>
  </Space>
);

export default function SubmitJobPage() {
  const navigate = useNavigate();
  const { projectId, batchId } = useParams();
  const { state } = useLocation();

  const userName = useSelector((s) => s.header.userName);
  const userEmail = useSelector((s) => s.header.userEmail);

  const selectedMolecules = useMemo(() => state?.selectedMolecules || [], [state?.selectedMolecules]);
  const filterCriteria = useMemo(() => state?.filterCriteria || [], [state?.filterCriteria]);
  const jobToClone = useMemo(() => state?.jobToClone || null, [state?.jobToClone]);
  const selectionIdRef = useRef(jobToClone?.selection_id ?? null);
  const isCloneMode = !!jobToClone;

  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState({});
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [modalDefaultTab, setModalDefaultTab] = useState("glide");
  const [activeJobTab, setActiveJobTab] = useState("screening");

  const pageSize = 8;
  const pagedMols = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return selectedMolecules.slice(start, start + pageSize);
  }, [selectedMolecules, currentPage]);

  const [selForm] = Form.useForm();
  const [scrForm] = Form.useForm();
  const hasSelectionName = Form.useWatch("selection_name", selForm);
  const scrMethod = Form.useWatch("method", scrForm);
  const scrGridFile = Form.useWatch("gridfile", scrForm);
  const scrPhaseFile = Form.useWatch("phasefile", scrForm);
  const scrPhBase = Form.useWatch("ph_range", scrForm);
  const scrPhThreshold = Form.useWatch("ph_threshold", scrForm);

  const [admeModels, setAdmeModels] = useState([]);
  const [admeLoading, setAdmeLoading] = useState(false);
  const [checkedParents, setCheckedParents] = useState([]);
  const [childChoiceMap, setChildChoiceMap] = useState({});
  const admeDirtyRef = useRef(false);

  useEffect(() => {
    if (!jobToClone) return;

    selForm.setFieldsValue({
      selection_name: jobToClone.selection_name || "",
      selection_desc: jobToClone.selection_desc || "",
    });
    selForm.setFields([
      { name: "selection_name", touched: false },
      { name: "selection_desc", touched: false },
    ]);
  }, [jobToClone, selForm]);

  useEffect(() => {
    if (!jobToClone) return;

    scrForm.setFieldsValue({
      method: jobToClone.method || undefined,
      ph_range: String(jobToClone.ph_range),
      ph_threshold: String(jobToClone.threshold),
      gridfile: jobToClone.gridfile,
      phasefile: jobToClone.phasefile,
    });
    scrForm.setFields([
      { name: "method", touched: false },
      { name: "ph_range", touched: false },
      { name: "ph_threshold", touched: false },
      { name: "gridfile", touched: false },
      { name: "phasefile", touched: false },
    ]);
  }, [jobToClone, scrForm]);

  useEffect(() => {
    const method = (jobToClone?.method || "").toLowerCase();
    setActiveJobTab(method === "adme" ? "adme" : "screening");
  }, [jobToClone]);

  useEffect(() => {
    const smilesToFetch = pagedMols
      .filter((molecule) => molecule?.smile && !images[molecule.molecule_id])
      .map((molecule) => molecule.smile);

    if (!smilesToFetch.length) return;

    generateImagesFromSmiles(smilesToFetch)
      .then((urls) => {
        const newImages = {};
        pagedMols
          .filter((molecule) => molecule?.smile && !images[molecule.molecule_id])
          .forEach((molecule, index) => {
            newImages[molecule.molecule_id] = urls[index];
          });
        setImages((prev) => ({ ...prev, ...newImages }));
      })
      .catch(() => {});
  }, [pagedMols, images]);

  useEffect(() => {
    if (activeJobTab !== "adme" || admeModels.length) return;

    setAdmeLoading(true);
    getRequest("/riva/adme/models")
      .then((resp) => setAdmeModels(resp?.data || []))
      .catch(() => message.error("Failed to load ADME models"))
      .finally(() => setAdmeLoading(false));
  }, [activeJobTab, admeModels.length]);

  useEffect(() => {
    if (!jobToClone || jobToClone?.method !== "adme" || !admeModels.length) return;

    const props = jobToClone.properties || [];
    const nextChecked = new Set();
    const nextChildMap = {};

    props.forEach((property) => {
      const model = admeModels.find((item) => item.name === property.model_name);
      if (!model) return;

      nextChecked.add(model.id);
      if (property.result_columns?.[0]) {
        nextChildMap[model.id] = property.result_columns[0];
      }
    });

    setCheckedParents(Array.from(nextChecked));
    setChildChoiceMap(nextChildMap);
    admeDirtyRef.current = false;
  }, [jobToClone, admeModels]);

  const scrValid = useMemo(() => {
    if (!scrMethod) return false;
    const hasPh = !!scrPhBase && !!scrPhThreshold;

    if (scrMethod === "glide") return hasPh && !!scrGridFile;
    if (scrMethod === "phase") return hasPh && !!scrPhaseFile;
    if (scrMethod === "glide-phase") return hasPh && !!scrGridFile && !!scrPhaseFile;
    return false;
  }, [scrMethod, scrPhBase, scrPhThreshold, scrGridFile, scrPhaseFile]);

  const admeValid = useMemo(
    () => checkedParents.length > 0 && checkedParents.every((pid) => childChoiceMap[pid]),
    [checkedParents, childChoiceMap]
  );

  const canSubmitScreening = !!hasSelectionName && scrValid && !submitting;
  const canSubmitAdme = !!hasSelectionName && admeValid && !submitting;

  const handleModalOk = () => {
    setIsModalVisible(false);
    navigate(`/project/${projectId}/batch/${batchId}/results`, {
      state: { defaultTab: modalDefaultTab },
    });
  };

  const toggleParent = (mdlId, checked) => {
    admeDirtyRef.current = true;
    setCheckedParents((prev) => (checked ? [...prev, mdlId] : prev.filter((p) => p !== mdlId)));

    if (checked) {
      const model = admeModels.find((item) => item.id === mdlId);
      if ((model?.results || []).length === 1) {
        setChildChoiceMap((prev) => ({ ...prev, [mdlId]: model.results[0].name }));
      }
      return;
    }

    setChildChoiceMap((prev) => {
      const { [mdlId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const setChildColumn = (mdlId, col) => {
    admeDirtyRef.current = true;
    setChildChoiceMap((prev) => ({ ...prev, [mdlId]: col }));
  };

  const hasUnsavedJobChanges = () => (
    selForm.isFieldsTouched() ||
    scrForm.isFieldsTouched() ||
    admeDirtyRef.current
  );

  const handleCancelWithUnsavedCheck = () => {
    if (!hasUnsavedJobChanges()) {
      navigate(-1);
      return;
    }

    Modal.confirm({
      title: "Unsaved Changes",
      content: "You have unsaved changes. Are you sure you want to discard them?",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep Editing",
      onOk: () => navigate(-1),
    });
  };

  const buildSelectionBlock = () => {
    const { selection_name, selection_desc } = selForm.getFieldsValue();

    const cleanFilterCriteria = filterCriteria
      .filter((rule) => !isRemovedJobParent(rule.parent))
      .map((rule) => {
        const { parentLabel, childLabel, ...rest } = rule;
        let newChild = rest.child;

        if (rest.parent === "molecule_id" && rest.child === "molecule_id::id") {
          newChild = "molecule_id::molecule_id";
        } else if (rest.parent === "reward" && rest.child === "reward::value") {
          newChild = "reward::reward";
        } else if (rest.parent === "glide" || rest.parent === "phase") {
          const parts = rest.child.split("::");
          if (parts.length === 4 && parts[1] === "file") {
            newChild = `${parts[0]}::${parts[1]}::${parts[2]}::ph_threshold::${parts[3]}`;
          }
        }

        return { ...rest, child: newChild };
      });

    return {
      id: selectionIdRef.current,
      name: selection_name,
      description: selection_desc || "",
      filter: cleanFilterCriteria,
      smiles: selectedMolecules.map((molecule) => ({
        smileId: molecule.molecule_id,
        smileString: molecule.smile,
      })),
    };
  };

  const submitScreening = async () => {
    if (submitting) return;

    try {
      await selForm.validateFields();
      const values = await scrForm.validateFields();
      setSubmitting(true);

      const toOneDecimal = (value) =>
        Number.isFinite(Number(value)) ? parseFloat(Number(value).toFixed(1)) : undefined;

      const modelObj = {
        method: values.method,
        ph: toOneDecimal(values.ph_range),
        ph_threshold: toOneDecimal(values.ph_threshold),
        grid_file: values.gridfile,
        phase_file: values.phasefile,
      };
      const endpointMap = {
        glide: "/riva/sd/glide",
        phase: "/riva/sd/phase",
        "glide-phase": "/riva/sd/glide_phase",
      };
      const payload = {
        modelObj,
        selection: buildSelectionBlock(),
        userDetail: { userName, userEmail, projectId, batchId },
      };
      const resp = await postRequest(endpointMap[values.method], payload);

      if (typeof resp?.data?.selection_id === "number") {
        selectionIdRef.current = resp.data.selection_id;
      }
      setModalContent({
        title: "Submission Successful",
        message: resp?.data?.message || "Job submitted!",
        details: resp?.data,
      });
      setModalDefaultTab(values.method === "phase" ? "phase" : "glide");
      setIsModalVisible(true);
    } catch (err) {
      if (!err.errorFields) message.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAdme = async () => {
    if (submitting) return;

    try {
      await selForm.validateFields();
      setSubmitting(true);

      const propsArr = checkedParents.map((pid) => {
        const model = admeModels.find((item) => item.id === pid);
        return {
          model_id: model.id,
          model_name: model.name,
          result_columns: [childChoiceMap[pid]],
          input_params: {},
        };
      });

      const payload = {
        modelObj: { properties: propsArr },
        selection: buildSelectionBlock(),
        userDetail: { userName, userEmail, projectId, batchId },
      };
      const resp = await postRequest("/riva/adme", payload);

      if (typeof resp?.data?.selection_id === "number") {
        selectionIdRef.current = resp.data.selection_id;
      }
      setModalContent({
        title: "Submission Successful",
        message: resp?.data?.message || "Job submitted!",
        details: resp?.data,
      });
      setModalDefaultTab("adme");
      setIsModalVisible(true);
    } catch (err) {
      if (!err.errorFields) message.error(err.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const tableCols = [
    { title: "Molecule ID", dataIndex: "molecule_id", key: "id", width: "25%" },
    {
      title: "2D Structure",
      key: "struct",
      width: "25%",
      align: "center",
      render: (_, record) =>
        images[record.molecule_id] ? (
          <img
            src={images[record.molecule_id]}
            alt=""
            style={{ height: 150, width: 150, objectFit: "contain" }}
          />
        ) : (
          <Spin size="small" />
        ),
    },
    {
      title: "SMILES String",
      dataIndex: "smile",
      key: "smile",
      render: (txt) => (
        <Space>
          <Text ellipsis style={{ maxWidth: 260 }}>{txt}</Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(txt);
              message.success("Copied");
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={submitting} tip="Submitting...">
      <div style={{ padding: 28, paddingTop: 100, background: "#f0f2f5" }}>
        <Space align="center" style={{ marginBottom: 16 }}>
          <Button onClick={handleCancelWithUnsavedCheck} icon={<ArrowLeftOutlined />} />
          <Title level={3} style={{ margin: 0 }}>Selection</Title>
        </Space>
        <BreadcrumbTrail />

        <Card style={{ marginBottom: 16 }}>
          <Form form={selForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Card style={{ height: "100%" }}>
                  <Title level={4} style={{ marginTop: 0, paddingBottom: 12 }}>Selection Details</Title>
                  <Form.Item
                    name="selection_name"
                    label={<FormLabel label="Selection Name" tooltip="Short identifier for this set of molecules." />}
                    rules={[{ required: true, message: "Please enter a name" }]}
                  >
                    <Input placeholder="e.g. Round-1 top 50" showCount maxLength={50} disabled={isCloneMode} />
                  </Form.Item>
                  <Form.Item
                    name="selection_desc"
                    label={<FormLabel label="Selection Description" tooltip="Optional longer description." />}
                  >
                    <Input.TextArea
                      rows={1}
                      maxLength={120}
                      showCount
                      placeholder="Why did you choose these molecules?"
                      disabled={isCloneMode}
                    />
                  </Form.Item>
                </Card>
              </Col>
              <Col span={12}>
                <Card style={{ height: "100%" }}>
                  <Title level={4} style={{ marginTop: 0 }}>Filter Criteria</Title>
                  <FilterCriteriaDisplay rules={filterCriteria} />
                </Card>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card>
          <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>Selected Molecules ({selectedMolecules.length})</Title>
            </Col>
            <Col>
              <Radio.Group value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                <Radio.Button value="grid"><AppstoreOutlined /> Grid</Radio.Button>
                <Radio.Button value="table"><TableOutlined /> Table</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>

          {viewMode === "grid" ? (
            <>
              <Row gutter={[16, 16]}>
                {pagedMols.map((mol) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={mol.molecule_id}>
                    <Card size="small" title={mol.molecule_id}>
                      <div style={{ textAlign: "center", marginBottom: 8, height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {images[mol.molecule_id] ? (
                          <img src={images[mol.molecule_id]} alt="" style={{ maxHeight: 150, maxWidth: "100%", objectFit: "contain" }} />
                        ) : (
                          <Spin />
                        )}
                      </div>
                      <Text copyable={{ text: mol.smile, onCopy: () => message.success("Copied") }} ellipsis={{ tooltip: mol.smile }} style={{ display: "block" }}>
                        {mol.smile}
                      </Text>
                    </Card>
                  </Col>
                ))}
              </Row>
              {selectedMolecules.length > pageSize && (
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={selectedMolecules.length}
                  onChange={setCurrentPage}
                  style={{ marginTop: 16, textAlign: "right" }}
                />
              )}
            </>
          ) : (
            <Table dataSource={selectedMolecules} columns={tableCols} rowKey="molecule_id" pagination={{ pageSize: 10 }} size="small" />
          )}
        </Card>

        <Title level={4} style={{ marginTop: 24, marginBottom: 16 }}>Choose a Job to Run</Title>

        <Tabs activeKey={activeJobTab} onChange={setActiveJobTab} tabPosition="left" tabBarWidth="30%">
          <TabPane tab="Screening" key="screening">
            <Card>
              <Form form={scrForm} layout="vertical">
                <Form.Item
                  name="method"
                  label={<FormLabel label="Method" tooltip="Select a screening method" />}
                  rules={[{ required: true, message: "Choose method" }]}
                >
                  <Select placeholder="method">
                    <Option value="glide">Glide Docking</Option>
                    <Option value="phase">Pharmacophore Screening</Option>
                    <Option value="glide-phase">Glide Docking + Pharmacophore Screening</Option>
                  </Select>
                </Form.Item>

                {scrMethod && (
                  <>
                    <Divider orientation="left" plain>LigPrep</Divider>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item
                          name="ph_range"
                          label={<FormLabel label="pH value" tooltip="Select a pH value" />}
                          rules={[{ required: true, message: "Select pH value" }]}
                        >
                          <Select placeholder="Select pH">
                            <Option value="6">6</Option>
                            <Option value="7">7</Option>
                            <Option value="8">8</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="ph_threshold"
                          label={<FormLabel label="Threshold (+/-)" tooltip="Select a pH threshold" />}
                          rules={[{ required: true, message: "Select threshold" }]}
                        >
                          <Select placeholder="+/-">
                            <Option value="0.0">+/- 0.0</Option>
                            <Option value="1.0">+/- 1.0</Option>
                            <Option value="2.0">+/- 2.0</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                {["glide", "glide-phase"].includes(scrMethod) && (
                  <Form.Item
                    name="gridfile"
                    label={<FormLabel label="GRIDFILE (.zip)" tooltip="Upload your grid file" />}
                    rules={[{ required: true, message: "Upload grid" }]}
                  >
                    <FileUploadField projectId={projectId} fileType="Gridfiles" accept=".zip" maxSizeMB={10} />
                  </Form.Item>
                )}
                {["phase", "glide-phase"].includes(scrMethod) && (
                  <Form.Item
                    name="phasefile"
                    label={<FormLabel label="Hypothesis (.zip)" tooltip="Upload your hypothesis file" />}
                    rules={[{ required: true, message: "Upload hypothesis" }]}
                  >
                    <FileUploadField projectId={projectId} fileType="Phasefiles" accept=".zip" maxSizeMB={10} />
                  </Form.Item>
                )}

                <Row justify="end">
                  <Space>
                    <Button onClick={handleCancelWithUnsavedCheck}>Cancel</Button>
                    <Button type="primary" disabled={!canSubmitScreening} onClick={submitScreening}>Submit</Button>
                  </Space>
                </Row>
              </Form>
            </Card>
          </TabPane>

          <TabPane tab="ADME" key="adme">
            <Card>
              {admeLoading ? (
                <Spin />
              ) : (
                <>
                  <Row gutter={[16, 12]}>
                    {admeModels.map((model) => {
                      const isChecked = checkedParents.includes(model.id);
                      const childCols = (model.results || []).map((result) => result.name);
                      const onlyOne = childCols.length === 1;

                      return (
                        <Col key={model.id} xs={24} sm={12}>
                          <div style={{ border: "1px solid #f0f0f0", borderRadius: 4, padding: 8, height: "100%" }}>
                            <Checkbox checked={isChecked} onChange={(e) => toggleParent(model.id, e.target.checked)}>
                              {model.name}
                            </Checkbox>
                            {isChecked && (
                              <div style={{ marginTop: 6, paddingLeft: 24 }}>
                                {onlyOne ? (
                                  <Text type="secondary">{childCols[0]}</Text>
                                ) : (
                                  <Select
                                    style={{ width: "100%" }}
                                    placeholder="Select result column"
                                    value={childChoiceMap[model.id]}
                                    onChange={(val) => setChildColumn(model.id, val)}
                                  >
                                    {childCols.map((column) => (
                                      <Option key={column} value={column}>{column}</Option>
                                    ))}
                                  </Select>
                                )}
                              </div>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                  <Row justify="end" style={{ marginTop: 16 }}>
                    <Space>
                      <Button onClick={handleCancelWithUnsavedCheck}>Cancel</Button>
                      <Button type="primary" disabled={!canSubmitAdme} onClick={submitAdme}>Submit</Button>
                    </Space>
                  </Row>
                </>
              )}
            </Card>
          </TabPane>
        </Tabs>

        <Modal
          title="Submission Successful"
          open={isModalVisible}
          onOk={handleModalOk}
          closable={false}
          okText="OK"
          footer={[
            <Button key="close" onClick={() => setIsModalVisible(false)}>Close</Button>,
            <Button key="submit" type="primary" onClick={handleModalOk}>View Results</Button>,
          ]}
          cancelButtonProps={{ style: { display: "none" } }}
        >
          {modalContent.details && (
            <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label="Message">{modalContent.message}</Descriptions.Item>
              <Descriptions.Item label="Use-case ID(s)">
                {Array.isArray(modalContent.details.usecase_id)
                  ? modalContent.details.usecase_id.join(", ")
                  : modalContent.details.usecase_id}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted By">{modalContent.details.submitted_id}</Descriptions.Item>
              <Descriptions.Item label="Submitted Time">
                {dayjs(modalContent.details.submitted_time).format("YYYY-MM-DD HH:mm:ss")}
              </Descriptions.Item>
              {typeof selectionIdRef.current === "number" && (
                <Descriptions.Item label="Selection ID">{selectionIdRef.current}</Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Modal>
      </div>
    </Spin>
  );
}
